import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    useWindowDimensions,
    Platform,
    View
} from "react-native";
import type ISidebarProviderProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useStableCallback
} from "../../../../helpers/interaction";
import type {
    SidebarContextType
} from "../../type";
import {
    getIsSidebarEditableTarget,
    getIsSidebarShortcutMatch,
    SIDEBAR_DEFAULT_SHORTCUT,
    getSidebarPersistedOpen,
    setSidebarPersistedOpen
} from "../../util";
import {
    SidebarContext
} from "../../context";

const SidebarProvider: FC<ISidebarProviderProps> = ({
    shortcut = SIDEBAR_DEFAULT_SHORTCUT,
    variant = "sidebar",
    isOpen: isOpenProp,
    compactBreakpoint,
    persistKey = null,
    customLocalize,
    wideBreakpoint,
    isDefaultOpen,
    onOpenChange,
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        width
    } = useWindowDimensions();

    const sidebarId = useId();

    const currentCompactBreakpoint = compactBreakpoint ?? shapeConfigs.compactBreakpoint;

    const currentWideBreakpoint = wideBreakpoint ?? shapeConfigs.wideBreakpoint;

    const isPhone = Platform.OS !== "web" || width < currentCompactBreakpoint;

    const [
        isDesktopOpen,
        setIsDesktopOpen
    ] = useState(() => getSidebarPersistedOpen({
        persistKey
    }) ?? isDefaultOpen ?? width >= currentWideBreakpoint);

    const [
        isPhoneOpen,
        setIsPhoneOpen
    ] = useState(false);

    const isOpen = isPhone ? isPhoneOpen : (isOpenProp ?? isDesktopOpen);

    const handleOpenChange = useStableCallback((nextIsOpen: boolean) => {
        if(isPhone) {
            setIsPhoneOpen(nextIsOpen);
        }

        if(isOpenProp === undefined && !isPhone) {
            setIsDesktopOpen(nextIsOpen);

            setSidebarPersistedOpen({
                isOpen: nextIsOpen,
                persistKey
            });
        }

        onOpenChange?.(nextIsOpen);
    });

    const handleToggle = useStableCallback(() => {
        handleOpenChange(!isOpen);
    });

    const handleToggleRef = useRef(handleToggle);

    handleToggleRef.current = handleToggle;

    const shortcutKey = shortcut ? shortcut.join("+") : "";

    useEffect(() => {
        if(Platform.OS !== "web" || !shortcut) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if(event.defaultPrevented || event.repeat || getIsSidebarEditableTarget(event.target)) {
                return;
            }

            if(!getIsSidebarShortcutMatch({
                shortcut,
                event
            })) {
                return;
            }

            event.preventDefault();

            handleToggleRef.current();
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [
        shortcutKey
    ]);

    const currentShortcut = useMemo(() => shortcut, [
        shortcutKey
    ]);

    const context = useMemo<SidebarContextType>(() => ({
        state: isOpen ? "expanded" : "collapsed",
        shortcut: currentShortcut,
        handleOpenChange,
        customLocalize,
        handleToggle,
        customTheme,
        sidebarId,
        isPhone,
        variant,
        isOpen
    }), [
        currentShortcut,
        handleOpenChange,
        customLocalize,
        handleToggle,
        customTheme,
        sidebarId,
        isPhone,
        variant,
        isOpen
    ]);

    const {
        container: containerDynamicStyle
    } = useStyles({
        variant,
        colors
    });

    return <SidebarContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {children}
        </View>
    </SidebarContext.Provider>;
};
export default SidebarProvider;
